// v0.5.0i+6 — Per-row resolver for Family B review-queue rows.
//
// v0.5.2 — the inline expand-below form was replaced with a Shadcn
// Popover anchored to the "Resolve" button. This kills the vertical
// layout shift that pushed every row below it down the page (the
// operator-reported clutter). Behaviour, testids, and the audit
// contract are unchanged: a per-row acknowledge posts the single
// txn_id to /api/runs/calc/{calcId}/review-acknowledge; the row's
// bucket is NOT changed — the acknowledgement itself is the audit
// fact that the operator reviewed the row and chose to keep it.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { authClient } from "../../lib/authApi";
import { errorMessage } from "../../lib/errorMessage";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { toast } from "../ui/sonner";
import { FxTreatmentAmend } from "./FxTreatmentAmend";
import { VatNumberAmend } from "./VatNumberAmend";
import { presetsFor } from "../../lib/reviewReasonPresets";

export const PerRowResolver = ({
  tid, flagType, calcId, calcFrozen, acked, onResolved, onInspect = null,
  hideChip = false,
  fxAmendable = false, fxReinvoiced = false, vatAmendable = false,
  groupRemaining = null, groupTotal = null,
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [preset, setPreset] = useState("");
  const presets = presetsFor(flagType);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const inspectBtn = onInspect ? (
    <button
      type="button"
      data-testid={`rg-inspect-${tid}`}
      onClick={() => onInspect({ transaction_ids: [tid], txn_count: 1 })}
      title={t("ui.review_grouped.inspect_row_tooltip", { defaultValue: "Inspect source transaction" })}
      className="text-[10px] px-2 py-1 rounded border border-slate-300 dark:border-slate-700 hover:border-teal-400 dark:hover:border-teal-500 hover:text-teal-700 dark:hover:text-teal-300 text-slate-600 dark:text-slate-300"
    >
      {t("ui.review_grouped.inspect_row_btn", { defaultValue: "Inspect" })}
    </button>
  ) : null;

  const amendBtn = (fxAmendable && !calcFrozen) ? (
    <FxTreatmentAmend
      tid={tid}
      calcId={calcId}
      reinvoiced={fxReinvoiced}
      onResolved={onResolved}
    />
  ) : null;

  // v0.6 — the buyer-VAT-number correction lives ALONGSIDE the plain
  // acknowledge (never instead of it): the operator can either try a
  // corrected number (VatNumberAmend) OR confirm-and-keep via the Resolve
  // button below, which is what the freeze gate (_review_gate.py) counts.
  const vatAmendBtn = (vatAmendable && !calcFrozen) ? (
    <VatNumberAmend tid={tid} calcId={calcId} onResolved={onResolved} />
  ) : null;

  // Same validation contract as bulk-ack reason + the v0.5.0
  // template-text validator on the backend: minimum length 10
  // (audit-readable), no angle-bracket characters (defensive XSS
  // + unfilled <TOKEN> placeholders).
  const cleaned = reason.trim();
  const valid = cleaned.length >= 10
    && !/<[A-Z_]+>/.test(cleaned)
    && !/[<>]/.test(cleaned);

  // v0.6.10 — say WHY the button is disabled, live.
  //
  // Operator-reported as "this blocking flag I cannot resolve". The
  // popover was opening correctly and the button was correctly
  // disabled — but nothing said so. The placeholder carried the rules
  // ("Minimum 10 characters..."), and being ~140 characters it filled
  // the 80px box and grew its own scrollbar, so an empty textarea
  // LOOKED like one that was already filled in. The operator saw a
  // full box and a dead button and reasonably concluded the feature
  // was broken.
  //
  // Derived from `valid`'s own clauses rather than restated, so the
  // hint cannot drift from the rule it explains.
  const disabledReason = (() => {
    if (valid || busy) return null;
    // v0.6.10 — no hint for "nothing chosen yet": the dropdown right
    // above already reads "Choose a reason…", and repeating it under a
    // greyed button is the kind of narration that makes a small panel
    // feel like a document.
    if (!cleaned) return null;
    if (cleaned.length < 10) return t("ui.review_grouped.row_resolve_need_more", {
      defaultValue: "{{n}} more character(s) needed.",
      n: 10 - cleaned.length,
    });
    return t("ui.review_grouped.row_resolve_no_brackets", {
      defaultValue: "Remove the < > characters.",
    });
  })();

  if (acked) {
    return (
      <div className="flex items-center gap-2">
        {hideChip ? (
          <span data-testid={`rg-done-${tid}`} data-resolved="true" className="text-xs text-emerald-700 dark:text-emerald-300">
            ✓ {t("ui.review_grouped.row_resolved_label", { defaultValue: "Reviewed" })}
          </span>
        ) : (
        <span
          data-testid={`rg-txn-${tid}`}
          data-resolved="true"
          className="px-2 py-1 rounded border bg-emerald-50 border-emerald-200 text-emerald-700 dark:bg-emerald-950/40 dark:border-emerald-800 dark:text-emerald-200 truncate text-[10px] tabular-nums"
          title={tid}
        >
          ✓ {tid.slice(0, 12)}…
        </span>
        )}
        {amendBtn}
        {vatAmendBtn}
        {inspectBtn}
      </div>
    );
  }

  const reset = () => { setReason(""); setPreset(""); setErr(null); };

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setErr(null);
    try {
      await authClient.post(`/runs/calc/${calcId}/review-acknowledge`, {
        flag_type: flagType,
        txn_ids: [tid],
        reason: cleaned,
      });
      setOpen(false);
      reset();
      toast.success(t("ui.review_grouped.row_resolved_toast", { defaultValue: "Row acknowledged" }), {
        description: `${tid.slice(0, 12)}… — recorded in the audit trail.`,
      });
      if (onResolved) await onResolved();
    } catch (e) {
      const msg = errorMessage(e);
      setErr(msg);
      toast.error(t("ui.review_grouped.row_resolve_failed", { defaultValue: "Could not acknowledge row" }), { description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="col-span-2 md:col-span-3">
      <div className="flex items-center gap-2 flex-wrap">
        {!hideChip && (
        <span
          data-testid={`rg-txn-${tid}`}
          data-resolved="false"
          className="px-2 py-1 rounded border bg-slate-100 border-slate-200 text-slate-700 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-200 truncate text-[10px] tabular-nums"
          title={tid}
        >
          {tid.slice(0, 12)}…
        </span>
        )}
        {!calcFrozen && (
          <Popover
            open={open}
            onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid={`rg-resolve-open-${tid}`}
                className="text-[10px] px-2 py-1 rounded border border-slate-300 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"
              >
                {t("ui.review_grouped.row_resolve_btn")}
              </button>
            </PopoverTrigger>
            <PopoverContent
              data-testid={`rg-resolve-popover-${tid}`}
              className="w-80 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 space-y-2"
            >
              {/* v0.6.10 — operator-reported: "not clear what will
                  happen when I add text... operator needs to know what
                  he is doing and why and what will follow". The box
                  previously opened onto a placeholder and a greyed
                  button with no statement of the ACTION or its
                  CONSEQUENCE. Three short lines, in that order, before
                  anything is asked of them. */}
              <div className="text-xs font-bold text-slate-900 dark:text-slate-100">
                {t("ui.review_grouped.row_resolve_heading", {
                  defaultValue: "Keep this row as it is",
                })}
              </div>
              <div className="text-[11px] leading-snug text-slate-600 dark:text-slate-300">
                {t("ui.review_grouped.row_resolve_effect", {
                  defaultValue:
                    "No figure changes. The row keeps its current "
                    + "treatment — you are recording that you checked it "
                    + "and chose to keep it, which is what stops it "
                    + "blocking sign-off.",
                })}
              </div>
              {/* v0.6.10 — pick, don't write. See
                  lib/reviewReasonPresets for why "Other" survives. */}
              <select
                data-testid={`rg-resolve-preset-${tid}`}
                value={preset}
                onChange={(e) => {
                  setPreset(e.target.value);
                  setReason(e.target.value);
                }}
                className="w-full bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 text-slate-900 dark:text-slate-100 rounded px-2 py-1 text-[11px]"
              >
                <option value="">
                  {t("ui.review_grouped.reason_pick", {
                    defaultValue: "Choose a reason…",
                  })}
                </option>
                {presets.map((r) => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              {/* v0.6.10 — only shown when the operator is actually
                  typing. "Minimum 10 characters" on a dropdown is a
                  rule about a box that isn't there. */}
              {groupTotal ? (
                <div
                  data-testid={`rg-resolve-progress-${tid}`}
                  className="text-[10px] text-slate-500 dark:text-slate-400"
                >
                  {t("ui.review_grouped.row_resolve_progress", {
                    defaultValue:
                      "{{remaining}} of {{total}} rows in this group still "
                      + "need a decision.",
                    remaining: groupRemaining,
                    total: groupTotal,
                  })}
                </div>
              ) : null}
              {err && (
                <div
                  data-testid={`rg-resolve-error-${tid}`}
                  className="text-[10px] text-red-700 dark:text-red-300"
                >
                  {err}
                </div>
              )}
              {disabledReason && (
                <div
                  data-testid={`rg-resolve-hint-${tid}`}
                  className="text-[10px] text-amber-700 dark:text-amber-300"
                >
                  {disabledReason}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  data-testid={`rg-resolve-cancel-${tid}`}
                  onClick={() => { setOpen(false); reset(); }}
                  className="text-[10px] underline text-slate-600 dark:text-slate-300"
                >
                  {t("ui.review_grouped.row_resolve_cancel")}
                </button>
                <button
                  type="button"
                  data-testid={`rg-resolve-submit-${tid}`}
                  data-resolution-class="recordkeeping"
                  data-resolution-endpoint="POST /api/runs/calc/{calc_id}/review-acknowledge"
                  disabled={busy || !valid}
                  onClick={submit}
                  className="text-[10px] font-bold px-3 py-1 rounded bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white"
                >
                  {busy
                    ? t("ui.review_grouped.row_resolve_submitting")
                    : t("ui.review_grouped.row_resolve_submit")}
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )}
        {amendBtn}
        {vatAmendBtn}
        {inspectBtn}
      </div>
    </div>
  );
};

export default PerRowResolver;
